import {
  BUILDING_TYPE_LABELS,
  appointmentSlot,
  displayCadastralReference,
  inquiryPurposeLabel,
  inspectionTitle,
  resolveInspectionResult,
  SEWER_NETWORK_TYPE_LABELS,
  townOf,
} from '@ghp/domain'
import type {
  AssignedInquiry,
  ProposedAnomaly,
  SewerNetworkType,
  TabletBlock,
  SurveyV2,
  VocabularyEntry,
} from '@ghp/domain'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CountBadge,
  DataField,
  EmptyState,
  Field,
  FieldValue,
  InfoNote,
  Input,
  Page,
  PageBody,
  PageHeader,
  TabPanel,
  Tabs,
  UrgentTag,
} from '@ghp/ui'
import {
  Building2,
  CalendarClock,
  ClipboardCheck,
  Home,
  Info,
  ListChecks,
  Lock,
  MapPin,
  Paperclip,
  PencilRuler,
  Send,
  TriangleAlert,
  Undo2,
} from 'lucide-react'
import { useRef, useState, type ReactNode } from 'react'

import type { InspectionDraft, LocalMedium, OutboxState } from '../../../offline/data-db'
import { isOtherPersonPresent } from '../../../offline/met-person'
import { RESULT_PRESENTATION, SYNC_PRESENTATION, TOUR_STATE_PRESENTATION } from '../labels'
import { withAnomalyConclusion } from '../lib/anomaly-conclusion'
import type { TourState } from '../lib/list-filters'
import type { SchematicExportHandler } from '@ghp/schematic-editor'
import { hasOutdatedSchematicImage } from '@ghp/schematic'
import { AnomalyBuilder } from './AnomalyBuilder'
import { AnomalyPicker } from './AnomalyPicker'
import { AttachmentsTab, isAttachment, type AttachmentType } from './AttachmentsTab'
import { AttendeeCard, AttendeeFields } from './AttendeeCard'
import {
  ContactsCard,
  OwnerCard,
  Value,
  type ContactsDraft,
  type ContactsEditing,
} from './ContactsCard'
import { DwellingCard } from './DwellingCard'
import { InspectedBuildings } from './InspectedBuildings'
import { InspectionTab } from './InspectionTab'
import { NotYetSynced } from './NotYetSynced'
import { SchematicTab } from './SchematicTab'
import { SubmitTab } from './SubmitTab'
import { StormwaterCard } from './StormwaterCard'
import { SurveyForm } from './SurveyForm'
import s from './InspectionEditorScreen.module.css'

/**
 * L'éditeur de contrôle, **présentationnel pur** : tout arrive en props, aucune I/O. C'est
 * la séparation du back-office (`apps/back-office/src/features/validation/routes/
 * inspection-editor.tsx`), reprise telle quelle — le conteneur de route connaît Dexie et le
 * routeur, l'écran ne connaît que ce qu'on lui donne.
 *
 * Six onglets, dans l'ordre de travail de l'agent (#411, décision du 09/10/2026) : Infos ·
 * Relevé · Contrôle · Pièces jointes · Schéma · Transmettre. Il relève d'abord — le questionnaire
 * `survey_v2`, les bâtiments, les anomalies et les eaux de pluie —, puis conclut : « Contrôle » ne
 * garde que la conclusion et ses observations.
 */
export type EditorTab = 'infos' | 'inspection' | 'survey' | 'attachments' | 'schematic' | 'submit'

export interface InspectionEditorScreenProps {
  /** `null` quand la demande n'est pas dans la tournée téléchargée : l'écran le dit. */
  inquiry: AssignedInquiry | null
  /**
   * L'état du contrôle **dans la tournée**, calculé par le conteneur (`tourStateOf`) : `null`
   * avec la demande, qu'il se lit sur.
   *
   * ⚠️ Pas `inquiry.status`, et pas une dérivation d'ici : l'API cesse de servir la demande une
   * fois déposée, donc son statut en cache reste celui d'avant le dépôt. La pastille affichait
   * « Planifié » à côté du « Transmis » de la synchro, sur la fiche que l'agent venait de
   * transmettre (#181). La règle vit une seule fois, dans `lib/list-filters.ts`.
   */
  state: TourState | null
  /** Le brouillon local — il existe toujours, `createDraft` le crée au premier accès. */
  draft: InspectionDraft
  /** Le référentiel d'anomalies en cache (M17) — vide avant la première synchro. */
  vocabulary: VocabularyEntry[]
  /** Les pièces déjà prises sur ce contrôle, octets compris (D5). */
  media: LocalMedium[]
  /** L'état de la file pour ce contrôle ; `null` tant que rien n'a été transmis. */
  sync: OutboxState | null
  /** Le motif du dernier échec ou refus d'envoi, tel que la file l'a consigné (M21). */
  syncError?: string | null
  /** Une descente refusée ou hors contrat — l'agent saisit sur un référentiel possiblement incomplet. */
  pullWarning?: string | null
  /** Tentatives d'envoi déjà faites — le retour visible du bouton « Réessayer l'envoi ». */
  syncAttempts?: number
  /** Une écriture locale a échoué : le dire, sinon l'échec est indistinguable d'un succès. */
  writeError?: string | null
  /**
   * Affordance de retour, transmise à `PageHeader`. Un slot, pas une route : c'est ce qui
   * garde l'écran ignorant du routeur, donc montable seul dans un test.
   */
  back?: ReactNode
  /**
   * Toute saisie passe par ici — une seule voie d'écriture, donc un seul endroit où le
   * brouillon est persisté. Sans rappel, l'éditeur se présente en lecture seule.
   */
  onDraftChange?: (patch: Partial<InspectionDraft>) => void
  /**
   * Une pièce vient d'être ajoutée : au conteneur de la ranger dans Dexie.
   *
   * La légende voyage avec le fichier, et jusqu'ici : saisie dans la fenêtre d'ajout, elle
   * n'atteignait pas Dexie et la vignette retombait sur le nom du fichier.
   */
  onCapture?: (file: File, caption: string | null) => void
  /**
   * L'organisation autorise-t-elle à joindre une pièce **après** avoir transmis (M35) ?
   *
   * ⚠️ **Ce drapeau ne garde rien** : le serveur refuse de toute façon, en `409` nommé, et la
   * pièce reste sur la tablette. Ce qu'il évite est un travail perdu — cadrer une photo, la
   * légender, et se la voir refuser une fois rentré.
   *
   * ⚠️ **Défaut `false`** : une tablette qui n'a pas encore resynchronisé se comporte comme
   * avant plutôt que d'ouvrir un geste que le serveur refusera.
   */
  allowAttachmentsAfterSubmission?: boolean
  onMediumTypeChange?: (mediumId: string, type: AttachmentType) => void
  /** Le nom d'une pièce, donné ou repris sur sa vignette : au conteneur de l'écrire dans Dexie. */
  onMediumLabelChange?: (mediumId: string, label: string | null) => void
  /** L'agent a confirmé le retrait d'une pièce jointe : au conteneur de l'effacer de Dexie. */
  onMediumRemove?: (mediumId: string) => void
  /**
   * L'image d'archive du schéma vient d'être rendue par la tablette (M11) : au conteneur de
   * l'attacher comme média `schematic` — **une seule par contrôle**, réécrite à chaque export.
   */
  onSchematicExport?: SchematicExportHandler
  /** Le tracé est fait : au conteneur d'y joindre la position et de l'enregistrer (M20). */
  onSign?: (signature: Blob) => void
  /** L'agent a effacé le canvas : au conteneur de retirer aussi la signature rangée. */
  onSignatureClear?: () => void
  /**
   * Le chemin vers l'**Historique**, offert une fois le contrôle arrivé au bureau : le
   * prototype y conduit lui-même après la transmission (`transmit`, `go('historique')`).
   *
   * Un slot, pas une route — comme {@link InspectionEditorScreenProps.back} : c'est ce qui
   * garde l'écran ignorant du routeur, donc montable seul dans un test.
   */
  history?: ReactNode
  /** Transmettre — donc verrouiller (D4). Sans rappel, le bouton se présente désactivé. */
  onSubmit?: () => void
  /** Relancer l'envoi de la file sans attendre le minuteur de synchro. */
  onRetrySend?: () => void
  /** Rouvrir une fiche refusée par le serveur : la corriger est sa seule sortie. */
  onReopen?: () => void
  /**
   * Corriger les coordonnées de la demande **en ligne** (carte « Coordonnées ») — une écriture
   * directe, hors proposition (seconde exception à D4). Sans elle, les coordonnées se lisent.
   */
  contacts?: ContactsEditing
  /**
   * Reprendre un contrôle que **le bureau a renvoyé** (#228) — la fiche se déverrouille et la
   * correction repartira sous une clé d'envoi neuve. Sans rappel, le bandeau de renvoi dit
   * pourquoi le contrôle est revenu sans offrir de le reprendre.
   */
  onReopenReturned?: () => void
}

export function InspectionEditorScreen({
  inquiry,
  state,
  draft,
  vocabulary,
  media,
  sync,
  syncError,
  syncAttempts,
  writeError,
  pullWarning,
  back,
  history,
  onDraftChange,
  onCapture,
  allowAttachmentsAfterSubmission,
  onMediumTypeChange,
  onMediumLabelChange,
  onMediumRemove,
  onSchematicExport,
  onSign,
  onSignatureClear,
  onSubmit,
  onRetrySend,
  onReopen,
  contacts,
  onReopenReturned,
}: InspectionEditorScreenProps) {
  /**
   * Le constructeur d'anomalie est ouvert. **État local de l'écran**, comme l'onglet courant :
   * ce n'est pas une saisie, rien n'a à en survivre à un rechargement — le contrôle, lui, est
   * dans Dexie, et c'est ce que le constructeur y verse qui compte.
   */
  const [building, setBuilding] = useState(false)
  /** Le point d'eau sur lequel le constructeur s'ouvre, depuis « Relever l'anomalie » de la grille. */
  const [preset, setPreset] = useState<{ fixture: string; location: string } | undefined>()
  /**
   * L'onglet ouvert — **tenu ici**, et non par la barre d'onglets elle-même.
   *
   * Le constructeur d'anomalie remplace tout le sous-arbre de la fiche : une barre non
   * contrôlée se remonte à son retour et retombe sur le premier onglet. L'agent validait son
   * anomalie depuis « Relevé » et se retrouvait sur « Infos », sans rien qui lui dise si sa
   * saisie avait été prise.
   */
  const [tab, setTab] = useState<EditorTab>('infos')
  /**
   * Le défilement du relevé, pour la même raison que l'onglet : remonté au retour du constructeur,
   * il repartait en haut, et l'agent cherchait la carte qu'il venait de quitter (Sentry TERRAIN-B).
   */
  const surveyBody = useRef<HTMLDivElement | null>(null)
  const heldSurveyScroll = useRef<number | null>(null)
  /**
   * La correction des coordonnées en cours — tenue ici pour la même raison que l'onglet : le
   * panneau « Infos » se démonte au changement d'onglet, et la carte perdait ce qu'on y tapait.
   * Rangée **par demande** : l'éditeur rouvert sur une autre ne doit pas en hériter.
   */
  const [heldContacts, setHeldContacts] = useState<{
    inquiryId: string
    draft: ContactsDraft | null
  } | null>(null)
  const contactsDraft =
    heldContacts !== null && heldContacts.inquiryId === inquiry?.id ? heldContacts.draft : null
  const setContactsDraft = (next: ContactsDraft | null) => {
    if (inquiry !== null) setHeldContacts({ inquiryId: inquiry.id, draft: next })
  }
  // **Transmettre, c'est verrouiller** (D4) : `lockedAt` est posé par `enqueueSubmission`, et
  // la fiche devient immuable côté tablette — y compris après un redémarrage.
  const locked = draft.lockedAt !== null
  // Les blocs que la fiche de ce client ne demande pas (#355), résolus par le serveur. `?? []` : une
  // ligne mise en cache avant eux les relit absents (D22) ; sans demande en cache, toute la fiche.
  const tabletHiddenBlocks = inquiry?.tabletHiddenBlocks ?? []
  const tourState = state === null ? null : TOUR_STATE_PRESENTATION[state]
  // La pastille de la file, **tant que la file a quelque chose à dire**.
  //
  // ⚠️ `sent` n'en fait pas partie : « le serveur a rangé l'enveloppe » est le fait dont l'état
  // de tournée est déjà tiré (`tourStateOf`), et les deux tables nomment ce fait du même mot —
  // l'en-tête portait deux pastilles « Transmis » côte à côte. C'est le reste de #181 : l'en-tête
  // disait « Planifié » à côté de « Transmis », réparer la contradiction l'a changée en
  // répétition. L'état de tournée est le plus riche des deux — il distingue « Transmis » de
  // « Renvoyé par le bureau » et de « Validé », là où la file dit `sent` dans les trois cas.
  //
  // Les autres états restent : « En attente d'envoi », « Envoi… », « En échec » et « Refusé —
  // non enregistré » disent ce que l'état de tournée ignore — le contrôle n'est pas arrivé, et
  // c'est la tablette qui le sait.
  const syncing = sync === null || sync === 'sent' ? null : SYNC_PRESENTATION[sync]
  // La signature et l'image du schéma sont des médias **produits**, pas des pièces jointes :
  // les compter ici gonflerait le compteur de photos que l'agent n'a jamais prises. La règle
  // vit une seule fois, avec la liste qu'elle filtre (#181).
  const photoCount = media.filter(isAttachment).length
  /**
   * L'image d'archive du schéma, s'il y en a une (M11) — **une seule par contrôle**. Lue ici
   * plutôt que descendue par le conteneur : les pièces y sont déjà, et un second chemin pour la
   * même ligne finirait par en diverger.
   */
  const schematicImage = media.find((medium) => medium.type === 'schematic') ?? null
  /**
   * Ce que le contrôle conclut, **sur les deux onglets qui en parlent** — « Contrôle », où la
   * conclusion se décide, et « Transmettre », d'où elle part. C'est le partage du prototype
   * (`headRight`, `screens.js`) : ailleurs, la pastille commenterait un écran qui n'en dit rien,
   * et la barre de titre porte déjà l'état de tournée et celui de la file.
   */
  const conclusion =
    tab === 'inspection' || tab === 'submit'
      ? RESULT_PRESENTATION[resolveInspectionResult(draft).value]
      : null

  /**
   * Ce qui se lit **avant le contenu de l'onglet** : pourquoi la fiche est revenue, pourquoi
   * elle est fermée, ce qu'une écriture locale a raté.
   *
   * Posé dans le panneau et non entre la barre de titre et les onglets, comme le prototype le
   * pose dans chaque `.body.scroll` : la barre de titre et la barre d'onglets forment un seul
   * bloc blanc sous un seul filet, et glisser une bande entre les deux le couperait en deux.
   *
   * ⚠️ Avant le verrou : c'est la raison pour laquelle l'agent est revenu sur cette fiche, et
   * elle se lit avant ce qu'il a le droit d'y faire.
   *
   * ⚠️ **Borné par l'état de tournée, jamais par la seule présence du motif.** Le cache des
   * demandes garde une demande tant qu'elle porte une saisie (`offline/pull.ts`) et le
   * serveur cesse de servir la demande dès qu'elle repart : un motif écrit une fois y resterait
   * **à vie**, et la fiche annoncerait un renvoi en cours au-dessus de « consultation seule »
   * sur un contrôle transmis, puis validé. `returned` est le seul état qui prouve le renvoi —
   * la descente postérieure au dépôt (#181) —, et c'est celui que la pastille de l'en-tête
   * affiche : les deux ne peuvent plus se contredire.
   *
   * ⚠️ **L'un ou l'autre, jamais les deux.** Un contrôle renvoyé est verrouillé lui aussi, tant
   * que l'agent ne l'a pas repris ; le bandeau de verrou annoncerait alors « les agents ne
   * peuvent plus le modifier » sous la phrase qui demande de corriger. C'est le bandeau de
   * renvoi qui porte l'état *et* le geste — il ne se tait pas sur le verrou, il en donne la
   * sortie (#228).
   */
  const notices = (
    <>
      {state === 'returned' ? (
        <ReturnedBanner
          reason={inquiry?.rejectionReason ?? null}
          onReopen={locked ? onReopenReturned : undefined}
        />
      ) : (
        locked && <LockedBanner />
      )}

      {/* L'échec d'une écriture locale ne se devine pas : sans ce bandeau, il serait
          indistinguable d'un succès — la vignette n'apparaît pas, l'agent recommence. */}
      {writeError != null && (
        <p className={s.writeError} role="alert">
          <TriangleAlert size={18} aria-hidden />
          {writeError}
        </p>
      )}

      {/* Un référentiel qui ne se met pas à jour n'est pas une saisie perdue : `role="status"`,
          pas `alert` — l'agent peut continuer, il doit juste savoir sur quoi il saisit. */}
      {pullWarning != null && (
        <p className={s.writeError} role="status">
          <TriangleAlert size={18} aria-hidden />
          {pullWarning}
        </p>
      )}
    </>
  )
  /**
   * **La seule voie d'écriture de la fiche**, et donc le seul endroit où la conclusion se
   * réécrit quand les anomalies la déplacent (`withAnomalyConclusion`) : sélecteur de l'onglet
   * « Relevé » ou constructeur d'anomalie, les deux versent dans la même liste.
   *
   * `undefined` quand l'écran est monté sans rappel : c'est ce qui le présente en lecture
   * seule, et un rappel toujours défini le rendrait saisissable sans rien écrire nulle part.
   */
  const changeDraft =
    onDraftChange === undefined
      ? undefined
      : (patch: Partial<InspectionDraft>) => {
          onDraftChange(withAnomalyConclusion(draft, patch))
        }
  const readOnly = locked || changeDraft === undefined

  const tabs: { value: EditorTab; label: ReactNode }[] = [
    {
      value: 'infos',
      label: (
        <>
          <Info size={18} aria-hidden /> Infos
        </>
      ),
    },
    {
      value: 'survey',
      label: (
        <>
          <ListChecks size={18} aria-hidden /> Relevé
        </>
      ),
    },
    {
      value: 'inspection',
      label: (
        <>
          <ClipboardCheck size={18} aria-hidden /> Contrôle
        </>
      ),
    },
    {
      value: 'attachments',
      label: (
        <>
          <Paperclip size={18} aria-hidden /> Pièces jointes
          <CountBadge>{photoCount}</CountBadge>
        </>
      ),
    },
    {
      value: 'schematic',
      label: (
        <>
          <PencilRuler size={18} aria-hidden /> Schéma
        </>
      ),
    },
    {
      value: 'submit',
      label: (
        <>
          <Send size={18} aria-hidden /> Transmettre
        </>
      ),
    },
  ]

  /**
   * Verser au contrôle ce que le constructeur a composé — `addBuilderAnomalies` du prototype.
   *
   * ⚠️ **Une phrase déjà relevée ne se relève pas deux fois** : la grille produit un produit
   * cartésien, et un agent qui la rouvre pour ajouter une origine recocherait les destinations
   * d'avant. Le rapport imprimerait alors la même prescription trois fois.
   */
  function addAnomalies(proposals: ProposedAnomaly[], survey?: SurveyV2): void {
    const anomalies = [
      ...draft.anomalies,
      ...proposals
        .filter(({ sentence }) => !draft.anomalies.some((kept) => kept.sentence === sentence))
        // `manual` : c'est l'agent qui a coché, aucune règle (M18) ne l'a déduit du relevé.
        .map((proposal) => ({ ...proposal, manual: true })),
    ]

    // Le relevé suit quand le constructeur l'a modifié : un point d'eau compté sur place, des
    // eaux de pluie vers les eaux usées — une seule écriture, la conclusion relue d'un coup.
    changeDraft?.(survey === undefined ? { anomalies } : { anomalies, survey })
    closeBuilder()
  }

  function openBuilder(at?: { fixture: string; location: string }): void {
    heldSurveyScroll.current = surveyBody.current?.scrollTop ?? null
    setPreset(at)
    setBuilding(true)
  }

  function closeBuilder(): void {
    setBuilding(false)
    setPreset(undefined)
  }

  /**
   * Le constructeur prend **toute la surface**, comme dans le prototype : c'est une sous-page,
   * pas une fenêtre. Une grille de 52 cases dans une modale sur tablette laisserait la moitié des
   * colonnes sous le pli, et fermer la fenêtre par mégarde perdrait la saisie en cours.
   */
  if (building) {
    return (
      <AnomalyBuilder
        subtitle={
          <>
            <MapPin size={15} aria-hidden />
            {addressOf(inquiry)}
          </>
        }
        vocabulary={vocabulary}
        survey={draft.survey}
        preset={preset}
        anomalyEffects={inquiry?.anomalyEffects}
        onCancel={closeBuilder}
        onValidate={addAnomalies}
      />
    )
  }

  return (
    <Page>
      {/*
       * L'en-tête de contexte du prototype (`.ctx`) : le titre, l'adresse **sous** lui avec
       * son épingle, et à droite ce que le contrôle vaut. Pas de filet — la barre d'onglets
       * suit sur la même surface, et le prototype n'en dessine qu'un pour tout le bloc.
       *
       * Ni bouton « Modifier » ni « Enregistrer » ici, que le prototype y porte : la saisie
       * est rangée dans Dexie à chaque frappe (D21), donc il n'y a rien à enregistrer. Un
       * bouton qui ne fait rien laisserait croire à l'agent qu'il doit y penser — et qu'il
       * a perdu sa saisie s'il l'oublie. Le « Modifier » réel est celui de la carte
       * « Coordonnées » : lui enregistre une écriture serveur, il a donc un sens.
       */}
      <PageHeader
        back={back}
        // « Urgent » au-dessus du titre, sur sa propre ligne (BACK-OFFICE-X).
        eyebrow={inquiry?.urgent === true ? <UrgentTag /> : undefined}
        title={
          inquiry === null
            ? 'Contrôle inconnu'
            : // Un contrôle libre n'a ni référence ni demande : le bureau les lui donnera (#419).
              'freeInspection' in inquiry
              ? 'Contrôle libre'
              : inspectionTitle(inquiry)
        }
        subtitle={
          <>
            <MapPin size={15} aria-hidden />
            {addressOf(inquiry)}
          </>
        }
        divider={false}
      >
        {/* La conclusion d'abord, comme le prototype la range : c'est ce que le contrôle
         **vaut**, là où les deux autres disent où il en est. */}
        {conclusion !== null && <Badge tone={conclusion.tone}>{conclusion.label}</Badge>}
        {tourState !== null && <Badge tone={tourState.tone}>{tourState.label}</Badge>}
        {syncing !== null && <Badge tone={syncing.tone}>{syncing.label}</Badge>}
      </PageHeader>

      {/*
       * `layout="fill"` : la barre d'onglets appartient à la région qui **ne défile pas**,
       * avec la barre de titre et sur la même surface — le `.ctx { flex: none }` du prototype,
       * dont chaque onglet rend ensuite son propre `.body.scroll`. D'où le `PageBody` **dans**
       * chaque panneau, et non autour de tout le bloc.
       *
       * ⚠️ C'est ce qui a été mesuré au navigateur, pas ce qui se devine à la lecture : rangée
       * dans la zone défilante, la barre partait à `top: -407` au bas de l'onglet « Relevé ».
       * L'agent finissait son formulaire et devait le remonter entier pour changer d'onglet.
       *
       * L'éditeur ouvre sur « Infos » : l'agent arrive devant la maison, il vérifie d'abord
       * qu'il est au bon endroit.
       */}
      <Tabs layout="fill" tabs={tabs} value={tab} onValueChange={setTab}>
        <TabPanel value="infos">
          <PageBody>
            {notices}
            <InfosPanel
              inquiry={inquiry}
              hiddenBlocks={tabletHiddenBlocks}
              draft={draft}
              readOnly={locked}
              onDraftChange={changeDraft}
              contacts={contacts}
              contactsDraft={contactsDraft}
              onContactsDraftChange={setContactsDraft}
            />
          </PageBody>
        </TabPanel>
        {/* Relever d'abord, conclure ensuite (#411) : tout ce que l'agent constate est au relevé,
            dans l'ordre de la frame validée — le constructeur s'ouvre d'ici et y ramène. */}
        <TabPanel value="survey">
          <PageBody
            ref={(node) => {
              surveyBody.current = node
              if (node !== null && heldSurveyScroll.current !== null) {
                node.scrollTop = heldSurveyScroll.current
                heldSurveyScroll.current = null
              }
            }}
          >
            {notices}
            <SurveyForm
              survey={draft.survey}
              hiddenBlocks={tabletHiddenBlocks}
              vocabulary={vocabulary}
              disabled={readOnly}
              onChange={(survey) => changeDraft?.({ survey })}
              anomalies={draft.anomalies}
              onFlagFixture={
                readOnly ? undefined : (fixture, location) => openBuilder({ fixture, location })
              }
              beforeFixtures={
                <>
                  <InspectedBuildings
                    survey={draft.survey}
                    disabled={readOnly}
                    onChange={(survey) => changeDraft?.({ survey })}
                  />
                  <AnomalyPicker
                    anomalies={draft.anomalies}
                    disabled={readOnly}
                    onChange={(anomalies) => changeDraft?.({ anomalies })}
                    onNewAnomaly={readOnly ? undefined : () => openBuilder()}
                    survey={draft.survey}
                    vocabulary={vocabulary}
                  />
                </>
              }
              afterFixtures={
                <StormwaterCard
                  survey={draft.survey}
                  vocabulary={vocabulary}
                  anomalyEffects={inquiry?.anomalyEffects}
                  disabled={readOnly}
                  onChange={(survey) => changeDraft?.({ survey })}
                />
              }
            />
          </PageBody>
        </TabPanel>
        <TabPanel value="inspection">
          <PageBody>
            {notices}
            <InspectionTab
              draft={draft}
              disabled={readOnly}
              onDraftChange={changeDraft}
              vocabulary={vocabulary}
              hiddenBlocks={tabletHiddenBlocks}
            />
          </PageBody>
        </TabPanel>
        <TabPanel value="attachments">
          <PageBody>
            {notices}
            <AttachmentsTab
              media={media}
              // ⚠️ **Le seul onglet qu'un verrou n'éteint pas forcément** : GHP peut autoriser
              // les pièces après transmission (M35). Le reste de la fiche reste figé — une
              // saisie transmise est immuable (D4) —, et ce n'est pas une exception à cette
              // règle : une pièce jointe est un **ajout**, pas une correction du relevé.
              disabled={
                (locked && allowAttachmentsAfterSubmission !== true) || onCapture === undefined
              }
              onCapture={onCapture}
              onTypeChange={onMediumTypeChange}
              // Le nom est parti avec l'enveloppe : le reprendre après la transmission dirait
              // au bureau autre chose que ce qu'il a reçu (D4).
              onLabelChange={locked ? undefined : onMediumLabelChange}
              onRemove={locked ? undefined : onMediumRemove}
            />
          </PageBody>
        </TabPanel>
        {/* Le dessin vit dans le **relevé** (M11), donc il repart par la même voie que
            l'onglet « Relevé » : une seule écriture de `survey`, jamais deux. */}
        {/*
          ⚠️ `keepMounted` : **seul panneau du dépôt à en avoir besoin**, et pour une raison
          qui ne vaut que pour lui. Les autres descendent leur saisie dans Dexie, donc un
          démontage ne leur coûte rien. Celui-ci tient en plus une pile d'annulation, un zoom
          et une sélection qui ne vivent nulle part ailleurs : un aller-retour de deux
          secondes vers « Pièces jointes » les effaçait, et l'agent revenait devant un
          « Annuler » grisé sur un dessin qu'il venait de faire (#247). Le dessin, lui,
          n'était pas perdu — c'est le travail en cours qui l'était.

          ⚠️ Et c'est **le seul** panneau dont les bandeaux se gardent : restant monté, il
          poserait un second exemplaire de chaque dans le document — deux fois le même
          `role="alert"`, et une phrase de verrou que rien ne distingue de l'autre.
        */}
        <TabPanel value="schematic" keepMounted>
          <PageBody>
            {tab === 'schematic' && notices}
            <SchematicTab
              exportedAt={schematicImage?.takenAt ?? null}
              exportOutdated={hasOutdatedSchematicImage(draft.survey.schematic, media)}
              survey={draft.survey}
              disabled={locked || changeDraft === undefined}
              onChange={(survey) => changeDraft?.({ survey })}
              onExport={onSchematicExport}
            />
          </PageBody>
        </TabPanel>
        <TabPanel value="submit">
          <PageBody>
            {notices}
            <SubmitTab
              draft={draft}
              media={media}
              // ⚠️ `?? null` : une tablette dont le cache précède le champ le relit `undefined`,
              // et l'écran doit alors dire « aucun rendez-vous », pas tomber (D22).
              scheduledAt={inquiry?.scheduledAt ?? null}
              contact={inquiry?.contact ?? null}
              locked={locked}
              returned={state === 'returned'}
              sync={sync}
              syncError={syncError}
              syncAttempts={syncAttempts}
              history={history}
              vocabulary={vocabulary}
              onDraftChange={changeDraft}
              onSign={onSign}
              onSignatureClear={onSignatureClear}
              onSubmit={onSubmit}
              onRetrySend={onRetrySend}
              onReopen={onReopen}
              onReopenReturned={onReopenReturned}
            />
          </PageBody>
        </TabPanel>
      </Tabs>
    </Page>
  )
}

/**
 * Ce que le bureau demande de corriger, **verbatim et entre guillemets** (#169), et le geste qui
 * rend la fiche à l'agent (#228).
 *
 * Le motif est écrit par un valideur, pour cet agent-là, dans la langue du métier : le citer
 * tel quel est la seule façon de ne pas le déformer, et les guillemets disent à l'agent que la
 * phrase vient du bureau et non de l'application.
 *
 * **Le bouton se tient ici**, contre le motif, et pas seulement au bas de l'onglet
 * « Transmettre » : la correction se fait dans les onglets **avant** celui-là, et un agent qui
 * doit d'abord traverser la fiche pour la déverrouiller la parcourt une fois pour rien.
 *
 * ⚠️ **Le motif peut manquer là où le renvoi, lui, est prouvé** : la descente qui suit le dépôt
 * l'établit à elle seule (`tourStateOf`), tandis que `rejectionReason` manque aux lignes mises
 * en cache avant qu'il existe. Le bandeau dit alors le renvoi sans le citer — taire les deux
 * laisserait la fiche verrouillée sans un mot, ce qui est le défaut que #228 corrige.
 *
 * `role="status"` et non `alert` : rien n'est cassé et rien n'est perdu — le contrôle est à
 * reprendre, ce qui est du travail, pas un incident.
 */
function ReturnedBanner({ reason, onReopen }: { reason: string | null; onReopen?: () => void }) {
  return (
    <div className={s.returned}>
      <Undo2 size={18} aria-hidden className={s.returnedIcon} />
      <div className={s.returnedBody}>
        <p className={s.returnedText} role="status">
          <strong>Le bureau a renvoyé ce contrôle pour correction.</strong>
          {reason !== null && <> «&nbsp;{reason}&nbsp;»</>}
        </p>
        {onReopen !== undefined && (
          <Button tone="brand" size="sm" onClick={onReopen}>
            <Undo2 size={18} aria-hidden /> Reprendre le contrôle pour le corriger
          </Button>
        )}
      </div>
    </div>
  )
}

/** Le bandeau verbatim du prototype validé — le vocabulaire de l'agent, pas le nôtre. */
function LockedBanner() {
  return (
    <p className={s.locked} role="status">
      <Lock size={18} aria-hidden />
      Contrôle transmis — consultation seule. Les agents ne peuvent plus le modifier.
    </p>
  )
}

/**
 * Onglet « Infos » — ce que le back-office a noté, que le terrain ne propose pas : l'afficher sert
 * à vérifier qu'on est devant la bonne maison.
 *
 * ⚠️ **Les trois cartes du prototype depuis le 21/09/2026.** Ce commentaire écartait la
 * troisième — « Coordonnées de la personne présente » — au motif que « ses champs vivent dans
 * l'onglet Transmettre » et qu'il y aurait deux points de saisie pour un même état. Un test
 * terrain a montré que l'argument confondait **deux données distinctes** :
 *
 * - ici, **ce que le bureau a noté** — qui doit ouvrir, son numéro, à quel titre. C'est en
 *   lecture seule, et c'est ce dont l'agent a besoin **avant** de sonner : personne ne répond,
 *   il appelle. Sans elle il se présentait devant la maison sans savoir à qui parler.
 * - dans « Transmettre », **qui a effectivement signé** — une saisie, souvent quelqu'un d'autre.
 *
 * Les afficher tous les deux n'est donc pas un doublon : c'est précisément l'écart entre les deux
 * que le rapport doit pouvoir montrer.
 *
 * ⚠️ **Deux champs restent vides**, et non plus six : « Client », « Type de contrôle » et
 * « N° parcelle » descendent depuis le 22/09/2026, le modèle les portant enfin
 * (`Inquiry.client_id`, `Inquiry.purpose`, `Site.cadastral_reference`), et « Réseau » depuis
 * M43 (`Site.sewer_network_type`). Restent « Maître d'ouvrage » et « Entreprise » : seule leur
 * carte dit encore que des champs ne descendent pas ({@link NotYetSynced}).
 *
 * ⚠️ **La page 1 du rapport papier depuis #350** (frame Terrain 1.1b) : le numéro de dossier sur
 * deux lignes, la communauté de communes que l'agent saisit, le propriétaire complet et les
 * caractéristiques de l'habitation, **déplacées** du relevé. Ces deux saisies vont dans le relevé
 * (`survey`), comme celle de la personne présente : l'onglet n'est plus en lecture seule.
 *
 * Le parti ne change pas (décision du 21/08) : montrer la fiche du prototype dit à l'agent ce
 * qu'il aura, la tronquer laisserait croire qu'elle est complète.
 */
function InfosPanel({
  inquiry,
  hiddenBlocks,
  draft,
  readOnly,
  onDraftChange,
  contacts,
  contactsDraft,
  onContactsDraftChange,
}: {
  inquiry: AssignedInquiry | null
  /** Les blocs que la fiche de ce client ne demande pas — vide sans demande en cache (#355). */
  hiddenBlocks: readonly TabletBlock[]
  draft: InspectionDraft
  readOnly?: boolean
  onDraftChange?: (patch: Partial<InspectionDraft>) => void
  contacts?: ContactsEditing
  contactsDraft?: ContactsDraft | null
  onContactsDraftChange?: (draft: ContactsDraft | null) => void
}) {
  if (inquiry === null) {
    return (
      <div className={s.stack}>
        <EmptyState align="start" title="Demande absente de la tournée">
          Cette demande n'est pas dans le cache de la tablette. La saisie reste possible et sera
          rattachée à sa demande à la transmission.
        </EmptyState>
        {/* ⚠️ Ces saisies restent, sans demande en cache : une demande absente de la tournée
            n'empêche pas de contrôler (D22). La ComCom et l'habitation ont quitté le relevé, et
            la carte de la personne présente est la **seule** saisie de l'identité du signataire. */}
        <Card>
          <CardHeader icon={<Building2 size={19} />} title="Informations générales" />
          <CardBody>
            <div className={s.grid}>
              <DelegatingAuthorityField
                draft={draft}
                readOnly={readOnly}
                onDraftChange={onDraftChange}
              />
            </div>
          </CardBody>
        </Card>
        {!hiddenBlocks.includes('dwelling') && (
          <DwellingCard
            survey={draft.survey}
            disabled={readOnly === true || onDraftChange === undefined}
            onChange={(survey) => onDraftChange?.({ survey })}
          />
        )}
        <AttendeeCard draft={draft} readOnly={readOnly} onDraftChange={onDraftChange} />
      </div>
    )
  }

  return (
    <div className={s.stack}>
      <Card>
        <CardHeader icon={<Building2 size={19} />} title="Informations générales" />
        <CardBody>
          <div className={s.grid}>
            <Absent label="Maître d'ouvrage" />
            {/* ⚠️ `?? null` sur les trois : une tablette dont le cache précède ces champs les
                relit `undefined`, et la fiche doit s'ouvrir quand même (D22). */}
            <DataField label="Client">
              <Value>{inquiry.clientName ?? null}</Value>
            </DataField>
            <Absent label="Entreprise" />
            <DataField label="Type de contrôle">
              <Value>{inquiryPurposeLabel(inquiry.purpose ?? null)}</Value>
            </DataField>
            {/* Une seule colonne, la ComCom à sa droite (retour du 05/10) : la réf. GHP, puis la
                réf. client dessous, dans la même case. */}
            <DataField label="Numéro de dossier" hint="Réf. GHP, puis réf. client">
              <FieldValue mono readOnly multiline>
                <span>
                  {inquiry.reference ?? '—'}
                  <br />
                  {inquiry.caseReference ?? '—'}
                </span>
              </FieldValue>
            </DataField>
            <DelegatingAuthorityField
              draft={draft}
              readOnly={readOnly}
              onDraftChange={onDraftChange}
            />
          </div>
          <NotYetSynced className={s.note} />
        </CardBody>
      </Card>

      {/* ⚠️ `?? null` : une tablette dont le cache précède ces deux champs les relit `undefined`,
          et l'onglet sur lequel la fiche s'ouvre doit alors dire « aucun rendez-vous », pas tomber
          (D22). Rien ne réécrit cette ligne — `FIELD_VISIBLE_STATUSES` ne redescend pas un
          contrôle transmis —, donc elle garde la forme d'avant pour de bon. */}
      <AppointmentCard scheduledAt={inquiry.scheduledAt ?? null} />

      {/* ⚠️ `?? null` : une tablette dont le cache précède `owner` le relit `undefined` (D22). */}
      <OwnerCard owner={inquiry.owner ?? null} />

      <ContactsCard
        contact={inquiry.contact ?? null}
        owner={inquiry.owner ?? null}
        readOnly={readOnly}
        editing={contacts}
        draft={contactsDraft}
        onDraftChange={onContactsDraftChange}
        otherPersonPresent={isOtherPersonPresent(draft)}
        signerCapacity={draft.signerCapacity}
        onSignerCapacityChange={
          onDraftChange === undefined
            ? undefined
            : (signerCapacity) => onDraftChange({ signerCapacity })
        }
        onOtherPersonPresentChange={
          onDraftChange === undefined
            ? undefined
            : (present) => onDraftChange({ otherPersonPresent: present })
        }
        attendee={
          <AttendeeFields draft={draft} readOnly={readOnly} onDraftChange={onDraftChange} />
        }
      />

      {/* Un seul bloc par défaut (#555) : le signataire est un volet du contact, et n'a sa carte
          que si la demande n'a pas de contact à reprendre. */}
      {(inquiry.contact ?? null) === null && (
        <AttendeeCard draft={draft} readOnly={readOnly} onDraftChange={onDraftChange} />
      )}

      {/* ⚠️ Masquée, jamais vidée : ce qui y était saisi part avec le relevé (M21). */}
      {!hiddenBlocks.includes('dwelling') && (
        <DwellingCard
          survey={draft.survey}
          disabled={readOnly === true || onDraftChange === undefined}
          onChange={(survey) => onDraftChange?.({ survey })}
        />
      )}

      <Card>
        <CardHeader icon={<Home size={19} />} title="Coordonnées du bâtiment" />
        <CardBody>
          <div className={s.grid}>
            <DataField label="N° parcelle">
              {/* La parcelle du rapport (`displayCadastralReference`) : normalisée, et tirée de la
                  réf. GHP quand le dossier n'en porte pas. */}
              <Value mono>
                {displayCadastralReference({
                  cadastralReference: inquiry.cadastralReference ?? null,
                  reference: inquiry.reference,
                })}
              </Value>
            </DataField>
            {/* Le réseau **du dossier** (M43), pas celui que l'agent constate dans le relevé :
                rien ne recopie l'un dans l'autre. `?? null` : un cache d'avant le champ (D22). */}
            <DataField label="Réseau">
              <Value>{networkLabel(inquiry.sewerNetworkType ?? null)}</Value>
            </DataField>
            {/* Le type de bâti du dossier (#586) : lu à l'import ou choisi au bureau. */}
            <DataField label="Type de bâti">
              <Value>
                {inquiry.buildingType == null ? null : BUILDING_TYPE_LABELS[inquiry.buildingType]}
              </Value>
            </DataField>
            <DataField label="Commune">
              <Value>{townOf(inquiry.address)}</Value>
            </DataField>
            <DataField label="Code postal">
              <Value mono>{inquiry.address.postalCode}</Value>
            </DataField>
            <DataField label="Rue" className={s.wide}>
              <Value>{inquiry.address.street}</Value>
            </DataField>
          </div>
          {/* Verbatim du prototype, où le back-office porte la même phrase au même endroit :
            c'est la forme que le bureau attend, et une reformulation d'un seul côté ferait
            deux formats pour une même règle. */}
          <InfoNote className={s.note} icon={<Info size={14} />}>
            Format parcelle : 2 lettres + 4 chiffres (ex. ET0090). 1 seule lettre → 0C 0090.
          </InfoNote>
        </CardBody>
      </Card>
    </div>
  )
}

/**
 * « COMCOM » sur la fiche papier : la **communauté de communes**, ni la réf. GHP ni le client
 * (confirmé le 02/10/2026). Le bureau ne la connaît pas, l'agent la relève dans le relevé
 * (`survey.general.delegatingAuthority`) — d'où un champ qui ne dépend pas de la demande en cache.
 */
function DelegatingAuthorityField({
  draft,
  readOnly,
  onDraftChange,
}: {
  draft: InspectionDraft
  readOnly?: boolean
  onDraftChange?: (patch: Partial<InspectionDraft>) => void
}) {
  return (
    <Field label="Communauté de communes (ComCom)">
      {({ id }) => (
        <Input
          id={id}
          placeholder="—"
          // Non contrôlé, comme les champs du relevé : la frappe vit dans le DOM, le brouillon suit
          // à chaque touche.
          defaultValue={draft.survey.general?.delegatingAuthority ?? ''}
          disabled={readOnly === true || onDraftChange === undefined}
          onValueChange={(raw) =>
            onDraftChange?.({
              survey: {
                ...draft.survey,
                general: {
                  ...draft.survey.general,
                  delegatingAuthority: raw.trim() === '' ? null : raw,
                },
              },
            })
          }
        />
      )}
    </Field>
  )
}

/**
 * La carte « Rendez-vous » — **quand** se présenter.
 *
 * ⚠️ **Elle n'existe pas dans le prototype validé**, et l'écart est assumé : l'agent arrivait
 * jusqu'ici devant une fiche qui lui disait *où* aller sans jamais lui dire *quand*. La donnée
 * descend désormais avec la tournée (`assignedInquirySchema`).
 *
 * ⚠️ **L'heure vient d'`appointmentSlot`, pas d'un `toLocaleString` local.** Le back-office, la
 * tablette et la confirmation e-mail annoncent le même rendez-vous à la même personne : trois
 * mises en forme seraient trois occasions d'afficher trois heures. Le fuseau y est imposé
 * (`Europe/Paris`), donc une tablette mal réglée affiche quand même l'heure annoncée à l'usager.
 *
 * **Qui demander en sonnant** est passé à la carte « Coordonnées » (23/09), qui le corrige en
 * ligne : le montrer ici aussi ferait lire deux fois le même nom, dont un seul se modifie.
 */
function AppointmentCard({ scheduledAt }: { scheduledAt: AssignedInquiry['scheduledAt'] }) {
  const slot = scheduledAt === null ? null : appointmentSlot(scheduledAt)

  return (
    <Card>
      <CardHeader icon={<CalendarClock size={19} />} title="Rendez-vous" />
      <CardBody>
        {slot === null ? (
          <InfoNote icon={<CalendarClock size={15} />}>
            Aucun rendez-vous posé — le bureau ne l'a pas encore planifié.
          </InfoNote>
        ) : (
          <div className={s.grid}>
            <DataField label="Date">
              <Value>{slot.day}</Value>
            </DataField>
            <DataField label="Heure">
              <Value mono>{slot.time}</Value>
            </DataField>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

/**
 * Un champ du prototype dont la donnée n'existe **nulle part** encore — ni au modèle, ni à
 * l'API. La boîte reste, vide : elle dit où la valeur se rangera.
 */
function Absent({ label }: { label: string }) {
  return (
    <DataField label={label}>
      <Value>{null}</Value>
    </DataField>
  )
}

function networkLabel(network: SewerNetworkType | null): string | null {
  return network === null ? null : SEWER_NETWORK_TYPE_LABELS[network]
}

function addressOf(inquiry: AssignedInquiry | null): string {
  if (inquiry === null) return 'Demande absente de la tournée téléchargée'

  const line = [inquiry.address.street, townOf(inquiry.address)].filter(Boolean).join(' · ')

  return line === '' ? 'Adresse inconnue' : line
}
